import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { requestsApi } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from './ui/alert-dialog';
import { CheckCircle2, Circle, CircleDot, XCircle, ListChecks, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const REVIEW_STAGE_STATUSES = [
  'FT_PENDING_FINANCE', 'FT_PENDING_DIRECTOR', 'FT_PENDING_EXEC_DIR_REVIEW',
  'PENDING_DIRECTOR_REVIEW', 'PENDING_EXEC_DIRECTOR_REVIEW', 'PENDING_CUSTOM_REVIEW',
];
// After a stage rejection the request returns to the Chairman: initial flow → final-approval
// statuses; signature ROUND flow → READY_TO_EXECUTE (Chairman restarts the round).
const BACK_AT_CHAIRMAN_STATUSES = ['FT_PENDING_CHAIRMAN_FINAL', 'PENDING_FINAL_APPROVAL', 'READY_TO_EXECUTE', 'EXECUTED'];

// Phase 5: shows the Chairman-configured review flow progress and lets the current-stage
// reviewer Approve / Reject (reject returns the request to the Chairman).
export function ReviewStagesPanel({ request, onChange }) {
  const { user } = useAuth();
  const isChairman = user?.role === 'CHAIRMAN' || user?.role === 'ADMIN' || user?.role === 'CRD';
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  // iter155 — Resolve role + acted-by name into the new display format:
  //   "Executive Director (Dr. Maha Al-Qahtani)"  ← role primary, name parenthesised.
  // Falls back to the custom label if no role/acted-by are available.
  const renderStageLabel = (s) => {
    const roleLabel = (s.role || s.label || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const who = s.acted_by_name || (s.user_name ?? null);
    if (roleLabel && who) return `${roleLabel} (${who})`;
    return roleLabel || s.label || '—';
  };

  const stages = request?.review_stages || [];
  if (!stages.length) return null;

  // iter142 — parallel review: any pending stage is actionable in any order.
  const isParallel = (request?.review_mode === 'parallel');
  const hasAnyPending = stages.some(s => s.status === 'PENDING');
  const inReview = isParallel ? hasAnyPending : REVIEW_STAGE_STATUSES.includes(request.status);
  const rejectedStage = stages.find(s => s.status === 'REJECTED');
  const backAtChairman = BACK_AT_CHAIRMAN_STATUSES.includes(request.status) && rejectedStage;
  // iter140 — once configured, the review-stages summary stays visible for the LIFE of the
  // request (incl. after all signatures complete) so the audit trail is always inline.

  // Sequential: there's exactly one "current" pending stage; user can act only if they
  // match it. Parallel: the user's actionable stage is the FIRST pending stage they match
  // (by user_id or by role) — they can approve their stage out-of-order.
  const findMyStage = () => stages.find(s =>
    s.status === 'PENDING' && (
      user?.role === 'ADMIN' ||
      (s.user_id ? user?.id === s.user_id : user?.role === s.role)
    )
  );
  const sequentialCurrent = (!isParallel && inReview) ? stages.find(s => s.status === 'PENDING') : null;
  const myStage = isParallel ? (inReview ? findMyStage() : null) : sequentialCurrent;
  const canAct = !!myStage && (
    user?.role === 'ADMIN' ||
    (myStage.user_id ? user?.id === myStage.user_id : user?.role === myStage.role)
  );
  const isRoundReject = backAtChairman && ['READY_TO_EXECUTE', 'EXECUTED'].includes(request.status);

  const approve = async () => {
    setBusy(true);
    try {
      const res = await requestsApi.reviewStageApprove(request.id);
      toast.success(res.data?.message || 'Review stage approved');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const res = await requestsApi.reviewStageReject(request.id, reason.trim());
      toast.success(res.data?.message || 'Returned to the Chairman');
      setShowReject(false);
      setReason('');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  const StageIcon = ({ s }) => {
    if (s.status === 'APPROVED') return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (s.status === 'REJECTED') return <XCircle className="w-4 h-4 text-red-500" />;
    // iter142 — in parallel, every pending stage is "current". In sequential, only the
    // first pending stage gets the amber CircleDot.
    if (isParallel && s.status === 'PENDING') return <CircleDot className="w-4 h-4 text-amber-500" />;
    if (sequentialCurrent && s.order === sequentialCurrent.order) return <CircleDot className="w-4 h-4 text-amber-500" />;
    return <Circle className="w-4 h-4 text-slate-300" />;
  };

  return (
    <Card className="border-slate-200" data-testid="review-stages-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-700">Request Review</span>
          {isParallel && inReview && (
            <Badge className="bg-blue-100 text-blue-800 text-xs" data-testid="review-parallel-badge">
              Parallel review — {stages.filter(s => s.status === 'PENDING').length} of {stages.length} pending
            </Badge>
          )}
          {!isParallel && sequentialCurrent && (
            <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid="review-current-stage-badge">
              Awaiting {renderStageLabel(sequentialCurrent)}
            </Badge>
          )}
          {!inReview && !rejectedStage && stages.every(s => s.status === 'APPROVED') && (
            <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid="review-all-approved-badge">
              All reviews complete
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {stages.map((s, i) => (
            <div key={`${s.user_id || s.role}-${i}`} className="flex items-center gap-1.5" data-testid={`review-stage-chip-${s.user_id || s.role}`}>
              <StageIcon s={s} />
              <span className={`text-sm ${s.status === 'APPROVED' ? 'text-emerald-700' : s.status === 'REJECTED' ? 'text-red-600' : 'text-slate-600'}`}>
                {renderStageLabel(s)}
              </span>
            </div>
          ))}
        </div>

        {backAtChairman && rejectedStage && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="review-rejection-banner">
            <span className="font-semibold">{(rejectedStage.role || rejectedStage.label || '').replace(/_/g, ' ')}</span>
            {rejectedStage.acted_by_name ? <> ({rejectedStage.acted_by_name})</> : null} returned this request to the Chairman
            {/* iter155 — Reject reason is exposed ONLY to Chairman / Admin / CRD.
                Everyone else (Company / Coordinator / assignees) sees the neutral
                copy without the reason, matching the in-app/email visibility. */}
            {isChairman && rejectedStage.notes ? <> — “{rejectedStage.notes}”</> : null}.{' '}
            {isChairman && (
              isRoundReject
                ? 'The signature round was cancelled — start a new signature round to retry.'
                : 'Re-submit the final approval to restart the review flow.'
            )}
            {!isChairman && (
              <span className="text-slate-600"> Awaiting Chairman action.</span>
            )}
          </div>
        )}

        {canAct && (
          <div className="flex gap-2 pt-1">
            <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-stage-approve-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Approve {renderStageLabel(myStage)} Review
            </Button>
            <Button variant="outline" onClick={() => setShowReject(true)} disabled={busy}
              className="border-red-300 text-red-600 hover:bg-red-50" data-testid="review-stage-reject-btn">
              <XCircle className="w-4 h-4 mr-1" /> Reject (return to Chairman)
            </Button>
          </div>
        )}

        <AlertDialog open={showReject} onOpenChange={setShowReject}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Return to Chairman?</AlertDialogTitle>
              <AlertDialogDescription>
                The request will go back to the Chairman, who can adjust the configuration and
                retry. Please provide a reason.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rejection…"
              data-testid="review-stage-reject-reason"
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); reject(); }}
                disabled={busy || !reason.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="review-stage-reject-confirm"
              >
                {busy ? 'Returning…' : 'Return to Chairman'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

export default ReviewStagesPanel;
